/**
 * Lookalike audiences in the wizard: people similar to one of the licence's own audiences (the seed), offered
 * in the acquisition stage when the server offers them for the licence. A lookalike is an audience hypothesis
 * of kind `lookalike`; the chosen ones are kept in `salesFunnelSetup.lookalikeAudiences` (hypothesis id ->
 * entry, apart from the AI audiences so that AI audience actions never touch them) and their ids go to
 * `audienceSelections.acquisition`.
 */
import { hypothesisToAudience } from '@/utils/audience-builder'

export const LOOKALIKE_AUDIENCE_ID = '3e8b6f0c-5a2d-4c71-9f4e-8d1b2a6c7e90'
export const LOOKALIKE_KIND = 'lookalike'
export const LOOKALIKE_STAGE = 'acquisition'
/** Seed kind of a lookalike built from an audience of the ad account. */
export const ACCOUNT_SEED_KIND = 'account_audience'

export const LOOKALIKE_TEXT = {
    title: 'Lookalike audience',
    description:
        "People in your country who are similar to one of your own audiences. Meta's lookalike and audience expansion are turned off for this ad set, so only the lookalike is targeted.",
    qualityNote:
        'Lead quality not verified: this seed only shows that people were interested, not that they became good customers, so the lookalike can find more of the same leads.',
    creationNote:
        'Meta builds the lookalike when the campaign is published and fills it over a few hours; the ad set can run meanwhile. If the lookalike cannot be used (for example the seed became too small or was flagged), this ad set is not published and you will see a warning.',
    noSeed: 'None of your audiences is large enough for a lookalike yet.',
    countryUnknown: 'The country of your business location could not be determined, so no lookalike can be built.',
    switchLabel: 'Add a lookalike audience',
    accountGroupTitle: 'Audiences from your ad account',
    accountEmpty:
        'None of the audiences saved from your ad account can be used as a seed yet. Fetch your audiences from the ad account in the audience picker, then come back here.',
    accountQualityNote:
        'Lead quality not verified: we cannot tell whether the people in this audience became good customers, so the lookalike can find more of the same.',
}

export const LOOKALIKE_STATUS_TEXT = {
    ready: 'Ready',
    populating: 'Ready, Meta is still populating it',
    not_created: 'Not created yet',
    not_available: 'Not available',
    too_small: 'Too small',
    flagged: 'Flagged by Meta',
    unusable: 'Not usable on Meta',
    being_prepared: 'Being prepared',
    unknown: 'Status could not be checked',
}

/** Error codes of lookalike generation and the fixed messages shown for them. */
export const LOOKALIKE_ERROR_MESSAGES = {
    LOOKALIKES_DISABLED: 'Lookalike audiences are not available for this business.',
    LOOKALIKE_NOT_AVAILABLE: 'This audience cannot be used for a lookalike right now. Choose another one.',
    PUBLISHER_NOT_CONNECTED:
        'Your ad account connection needs to be renewed. Reconnect it in Media Settings and try again.',
}

const GENERIC_ERROR = 'The lookalike audience could not be prepared. Please try again.'

export const mapLookalikeError = (error) =>
    LOOKALIKE_ERROR_MESSAGES[error?.graphQLErrors?.[0]?.extensions?.code] || GENERIC_ERROR

const toArray = (value) => (Array.isArray(value) ? value : [])

/** The lookalike entries of the wizard, by hypothesis id. */
export const getLookalikeMap = (data) => data?.lookalikeAudiences || {}

/** Inputs a lookalike depends on, to detect one that no longer matches the wizard. */
export const buildLookalikeInputs = ({ publisherId, objective, location }) => ({
    publisherId: publisherId || null,
    objective: String(objective || '').toLowerCase(),
    location: location || null,
})

/** Whether a stored lookalike entry was prepared for other campaign details than `inputs`. */
export const isLookalikeStale = (entry, inputs) =>
    !!entry?.inputs && Object.keys(entry.inputs).some((key) => entry.inputs[key] !== inputs?.[key])

/**
 * The wizard entry of a lookalike hypothesis returned by the server.
 * @param {object} hypothesis
 * @param {object|null} [inputs] campaign details it was prepared for
 */
export const toLookalikeEntry = (hypothesis, inputs = null) => ({
    id: hypothesis.id,
    kind: LOOKALIKE_KIND,
    label: hypothesis.label,
    catalogueAudienceId: hypothesis.audience_id || LOOKALIKE_AUDIENCE_ID,
    audienceMode: hypothesis.audience_mode || 'manual',
    interests: [],
    seedKind: hypothesis.lookalike?.seed_kind || null,
    accountAudienceId: hypothesis.lookalike?.account_audience_id || null,
    seedLabel: hypothesis.lookalike?.seed_label || null,
    qualityVerified: hypothesis.lookalike?.quality_verified === true,
    country: hypothesis.lookalike?.country || null,
    ...(inputs ? { inputs } : {}),
})

/** The chosen lookalike entries (those selected in the acquisition stage). */
export const selectedLookalikes = (data) => {
    const selected = toArray(data?.audienceSelections?.[LOOKALIKE_STAGE])
    return Object.values(getLookalikeMap(data)).filter((entry) => selected.includes(entry.id))
}

/** Whether the wizard data has a lookalike selected. */
export const isLookalikeSelected = (data) => selectedLookalikes(data).length > 0

/** Seed of the chosen lookalikes, or null when none is chosen or its seed is unknown. */
export const chosenSeedKind = (data) => selectedLookalikes(data)[0]?.seedKind || null

/** The chosen seed as `{ kind, accountAudienceId }`; both null when no lookalike is chosen. */
export const chosenSeed = (data) => {
    const entry = selectedLookalikes(data)[0]
    return { kind: entry?.seedKind || null, accountAudienceId: entry?.accountAudienceId || null }
}

/** Whether two seeds (`{ kind, accountAudienceId }`) are the same one. */
export const isSameSeed = (a, b) =>
    !!a?.kind && a.kind === b?.kind && (a.accountAudienceId || null) === (b?.accountAudienceId || null)

/**
 * Wizard data with `hypotheses` (lookalike hypotheses from the server, prepared for `inputs`) as the chosen
 * lookalikes, replacing earlier ones; with none, the lookalikes are removed. Other selections are kept.
 * @param {object} data Sales Funnel Setup step data
 * @param {object[]} [hypotheses]
 * @param {object|null} [inputs]
 */
export const withLookalikes = (data, hypotheses = [], inputs = null) => {
    const current = data || {}
    const previous = getLookalikeMap(current)
    const selections = current.audienceSelections || {}
    const kept = toArray(selections[LOOKALIKE_STAGE]).filter((id) => !previous[id])
    const entries = toArray(hypotheses).map((hypothesis) => toLookalikeEntry(hypothesis, inputs))
    const rest = { ...current }
    delete rest.lookalikeAudiences
    return {
        ...rest,
        audienceSelections: { ...selections, [LOOKALIKE_STAGE]: [...kept, ...entries.map((entry) => entry.id)] },
        ...(entries.length > 0
            ? { lookalikeAudiences: Object.fromEntries(entries.map((entry) => [entry.id, entry])) }
            : {}),
    }
}

/** Catalogue audiences with the wizard's lookalikes appended. Same array when there are none. */
export const mergeLookalikeAudiences = (audiences, lookalikeMap) => {
    const entries = Object.values(lookalikeMap || {})
    if (entries.length === 0) return audiences
    const base = audiences || []
    const known = new Set(base.map((audience) => audience.id))
    const missing = entries.filter((entry) => !known.has(entry.id))
    return missing.length > 0 ? [...base, ...missing.map(hypothesisToAudience)] : audiences
}

/** One line about a seed for the wizard, from the server's read-only status. */
export const lookalikeSeedLine = (seed) => {
    if (!seed) return null
    const status = LOOKALIKE_STATUS_TEXT[seed.status] || LOOKALIKE_STATUS_TEXT.unknown
    const size =
        typeof seed.approximate_count_lower === 'number' && seed.approximate_count_lower >= 0
            ? ` · about ${seed.approximate_count_lower.toLocaleString('en-US')}+ people`
            : ''
    const reason = seed.message ? ` (${seed.message})` : ''
    return `${status}${size}${reason}`
}

/** "Similar to people who purchased", with the lead quality note for seeds that only show interest. */
export const lookalikeSeedLabel = (seed) =>
    `Similar to ${seed.label}${seed.quality_verified ? '' : '; lead quality not verified'}`

/** "Similar to Website visitors": the label of an audience of the ad account used as a seed. */
export const lookalikeAccountSeedLabel = (seed) => `Similar to ${seed.label}`
